import React, { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import MapComponent from '@components/Locations/MapComponent';
import { PageContext } from '@contexts/PageContext';
import { DEFAULT_MAP_CENTER } from '@utils/config';
import {
  getQueryParams,
  buildQueryString,
} from 'pubweb-smokey/dist/utils/utils';
import { runCommonValidation } from 'pubweb-smokey/dist/utils/validation';
import { desktop_breakpoint } from 'pubweb-smokey/dist/components/GridSystem/_vars_widths.js';
import ChevronDown from 'pubweb-smokey/dist/images/svg/iconography-16x16/chevron-dwn.svg';
import ChevronLeft from 'pubweb-smokey/dist/images/svg/iconography-16x16/chevron-lft.svg';
import ChevronRight from 'pubweb-smokey/dist/images/svg/iconography-16x16/chevron-right.svg';
import LocationPin from 'pubweb-smokey/dist/images/svg/iconography-16x16/location-line.svg';
import LoadingIndicator from '@components/Shared/LoadingIndicator/LoadingIndicator';
import useWindowResize from 'pubweb-smokey/dist/hooks/useWindowResize';
import { formatNumberWithCommas } from 'pubweb-smokey/dist/utils/formatters';
import { pushGTMEvent } from 'pubweb-smokey/dist/utils/analytics';
import { LocationsContext } from '@contexts/LocationsContext';
import LocationMarker from '@components/Locations/LocationMarker';
import { searchLand } from '@services/landService';
import { getLocationByIpOrPostalCode } from '@services/locationService';
import AreaHeader from '@components/Shared/AreaHeader/AreaHeader';
import { getSiteUrl } from '@utils/config';
import { trackListHub } from '@utils/listHub';
import LandListingItem from '@components/Land/LandListingItem';
import { LandSearchStyles } from './LandSearch.styled';

const AnchoredModal = dynamic(
  () => import('@components/Shared/Modal/AnchoredModal/AnchoredModal'),
  { ssr: false }
);

const Form = dynamic(() => import('@components/Shared/Form/Form'), {
  ssr: false,
});

const DropDownList = dynamic(
  () => import('@components/Shared/DropDownList/DropDownList'),
  {
    ssr: false,
  }
);

const CityLocationSuggestion = dynamic(
  () => import('@components/Shared/CityLocationList/CityLocationSuggestion'),
  {
    ssr: false,
  }
);

const radii = [5, 10, 15, 20, 25].map((r) => {
  return { text: r + ' miles', value: r };
});
const minPrices = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((p) => {
  return {
    text: `$${formatNumberWithCommas(p * 1000) || '0'}`,
    value: p * 1000,
  };
});
const maxPrices = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 'Any'].map((p) => {
  return parseInt(p)
    ? { text: `$${formatNumberWithCommas(p * 1000)}`, value: p * 1000 }
    : { text: `Any`, value: 'any' };
});
const sortOptions = [
  { text: 'Lowest Price', value: 0 },
  { text: 'Highest Price', value: 1 },
];

const getInitialSearchParams = (ssrInitialParams) => {
  const initialParams = ssrInitialParams || getQueryParams();
  initialParams.zip =
    parseInt(initialParams.zip) && initialParams.zip.length == 5
      ? initialParams.zip
      : null;

  initialParams.radius = parseInt(initialParams.radius) || 5;

  if (radii.filter((r) => r.value === initialParams.radius).length === 0) {
    initialParams.radius = Math.min(
      parseInt(initialParams.radius / 5) * 5 + 5,
      25
    );
  }
  initialParams.minPrice =
    initialParams.minPrice !== undefined
      ? parseInt(initialParams.minPrice) || 0
      : 0;

  // make sure it's a valid minPrice, if not, set it to the next 10,000's value, max 100,000
  if (
    minPrices.filter((price) => price.value === initialParams.minPrice)
      .length === 0
  ) {
    initialParams.minPrice = Math.min(
      parseInt(initialParams.minPrice / 10000) * 10000 + 10000,
      100000
    );
  }

  initialParams.maxPrice =
    initialParams.maxPrice !== undefined
      ? parseInt(initialParams.maxPrice) || 40000
      : 40000;

  // make sure it's a valid maxPrice, if not, set it to the next 10,000's value, max "Any"
  if (
    maxPrices.filter((price) => price.value === initialParams.maxPrice)
      .length === 0
  ) {
    initialParams.maxPrice = Math.min(
      parseInt(initialParams.maxPrice / 10000) * 10000 + 10000,
      110000
    );
    if (initialParams.maxPrice === 110000) {
      initialParams.maxPrice = 'any';
    }
  }

  if (initialParams.minPrice >= initialParams.maxPrice) {
    initialParams.minPrice = initialParams.maxPrice - 10000;
  }

  initialParams.page = (parseInt(initialParams.page) || 1) - 1;
  initialParams.sortBy = parseInt(initialParams.sortBy) == 1 ? 1 : 0;
  return initialParams;
};

const LandSearch = ({ ssrLandListings, ssrSearchPath }) => {
  const [zip, setZip] = useState(null);
  const [enteredZip, setEnteredZip] = useState(null);
  const [zipErrors, setZipErrors] = useState({});
  const [radius, setRadius] = useState(null);
  const [enteredRadius, setEnteredRadius] = useState(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [minPrice, setMinPrice] = useState(null);
  const [enteredMinPrice, setEnteredMinPrice] = useState(null);
  const [maxPrice, setMaxPrice] = useState(null);
  const [enteredMaxPrice, setEnteredMaxPrice] = useState(null);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [sortBy, setSortBy] = useState(0);
  const [enteredSortBy, setEnteredSortBy] = useState(0);
  const [showSortModal, setShowSortModal] = useState(false);
  const [pageIndex, setPageIndex] = useState(null);
  const [isSearchBarScrollMin, setIsSearchBarScrollMin] = useState(true);
  const [isSearchBarScrollMax, setIsSearchBarScrollMax] = useState(true);
  const [landListings, setLandListings] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedListing, setSelectedListing] = useState();
  const [showMap, setShowMap] = useState(false);
  const searchBarRef = useRef();
  const history = useRouter();
  const [mapOptions, setMapOptions] = useState({});
  const locationsContext = useContext(LocationsContext);

  useEffect(() => {
    const initialState = getInitialSearchParams();

    setZip(initialState.zip);
    setEnteredZip(initialState.zip);
    setRadius(initialState.radius);
    setEnteredRadius(initialState.radius);
    setMinPrice(initialState.minPrice);
    setEnteredMinPrice(initialState.minPrice);
    setMaxPrice(initialState.maxPrice);
    setEnteredMaxPrice(initialState.maxPrice);
    setPageIndex(initialState.page);
    setSortBy(parseInt(initialState.sortBy));
    setEnteredSortBy(initialState.sortBy);
    setLandListings(ssrLandListings);
  }, []);

  const updateLand = () => {
    setIsSearching(true);
    window.scrollTo({ top: 300, behavior: 'smooth' });

    pushGTMEvent('LandIt Search', 'Main CTA', {
      searchFilters: {
        zip: zip,
        radius: radius + ' Miles',
        minPrice: '$' + minPrice,
        maxPrice: maxPrice == 'any' ? 'Any' : '$' + maxPrice,
        page: pageIndex || 1,
        sortBy: sortBy
          ? 'Price: Highest to Lowest'
          : 'Price: Lowest to Highest',
      },
    });

    searchLand({
      zipCode: zip,
      radius,
      minPrice,
      maxPrice: parseInt(maxPrice) || null,
      resultsPerPage: 25,
      pageIndex,
      sortBy,
    }).then((result) => {
      if (pageIndex > result.pageCount) {
        setPageIndex(0);
      } else {
        setIsSearching(false);
        setLandListings(result);

        const listHubData = result.listings.map((listing) => {
          return { lkey: listing.listingKey };
        });
        trackListHub('submit', 'SEARCH_DISPLAY', listHubData);
      }
    });
  };

  useEffect(() => {
    if (landListings) {
      const listHubData = landListings.listings.map((listing) => {
        return { lkey: listing.listingKey };
      });
      trackListHub('submit', 'SEARCH_DISPLAY', listHubData);
    }
  }, []);

  const buildQueryParameters = () => {
    const queryBuilder = {
      zip: zip,
      radius: radius,
      minPrice: minPrice?.toString(),
      maxPrice: maxPrice?.toString(),
      page: pageIndex + 1,
      sortBy: sortBy.toString(),
    };

    return queryBuilder;
  };

  useEffect(() => {
    const {
      zip: initZip,
      radius: initRad,
      minPrice: initMinPrice,
      maxPrice: initMaxPrice,
      sortBy: initSortBy,
      pageIndex: initPage,
    } = getInitialSearchParams();

    if (
      (zip && zip !== initZip) ||
      (radius && radius !== initRad) ||
      (typeof minPrice === 'number' && minPrice !== initMinPrice) ||
      (maxPrice && maxPrice !== initMaxPrice) ||
      (typeof sortBy !== 'undefined' && sortBy !== initSortBy) ||
      (pageIndex && pageIndex !== initPage)
    ) {
      updateLand();
      locationsContext.actions.updatePostalCode(zip);

      history.replace(
        window.location.pathname + buildQueryString(buildQueryParameters())
      );
    }
  }, [zip, radius, minPrice, maxPrice, sortBy, pageIndex]);

  useEffect(() => {
    if (zip) {
      getLocationByIpOrPostalCode(zip)
        .then((result) => {
          const geocode = result;

          setMapOptions(() => {
            let zoom = 13 - radius / 5;
            let { lat, lng } = geocode
              ? {
                  lat: geocode.latitude,
                  lng: geocode.longitude,
                }
              : {
                  lat: DEFAULT_MAP_CENTER.lat,
                  lng: DEFAULT_MAP_CENTER.lng,
                };
            return {
              center: { lat, lng },
              draggable: true,
              draggableCursor: 'grab',
              draggingCursor: 'grabbing',
              scrollwheel: false,
              mapTypeControl: false,
              mapTypeControlOptions: {},
              streetViewControl: false,
              zoomControl: true,
              zoomControlOptions: {},
              zoom,
              optimized: false,
            };
          });
        })
        .catch((e) => {
          console.log(e);
        });
    }
  }, [landListings]);

  useWindowResize(() => {
    setShowMap(window.innerWidth > desktop_breakpoint);
  }, [desktop_breakpoint]);

  useEffect(() => {
    if (searchBarRef.current) {
      searchBarRef.current.addEventListener;
    }
  });

  const [touchTimeout, setTouchTimeout] = useState();

  const updateSearchBarScroll = (changeAmount) => {
    if (searchBarRef.current) {
      searchBarRef.current.scrollLeft += changeAmount;
    }
  };
  const startSearchBarScroll = (changeAmount) => {
    clearInterval(touchTimeout);

    const touchInterval = setInterval(() => {
      updateSearchBarScroll(changeAmount);
    }, 20);

    setTouchTimeout(touchInterval);
  };

  const endSearchBarScroll = () => {
    clearInterval(touchTimeout);
    setTouchTimeout(0);
  };

  const checkSearchBarScrollState = () => {
    if (searchBarRef.current) {
      setIsSearchBarScrollMin(searchBarRef.current.scrollLeft == 0);
      setIsSearchBarScrollMax(
        searchBarRef.current.scrollWidth - searchBarRef.current.scrollLeft ===
          searchBarRef.current.clientWidth
      );
    }
  };

  const validateZip = () => {
    const formErrors = runCommonValidation({
      zip: {
        value: enteredZip,
        touched: true,
      },
    });

    setZipErrors(formErrors);
    return !formErrors.hasErrors;
  };

  useEffect(checkSearchBarScrollState, []);

  const getSearchPath = () => {
    return (
      (typeof window != 'undefined'
        ? location.pathname + location.search
        : ssrSearchPath) || ''
    );
  };

  //left navigation only - clicks on street address, and pins
  const handleClickLeftNavigator = (listing) => {
    setSelectedListing(listing);
    setMapOptions({
      ...mapOptions,
      center: {
        lat: listing.geocodeLocation.latitude,
        lng: listing.geocodeLocation.longitude,
      },
    });
  };

  //map only - pin clicks
  const handleClickMapNavigator = (listing, index, numberOfListings) => {
    setSelectedListing(listing);

    const entryName = 'listing-item-' + (index + 1);
    const lastEntryName = 'listing-item-' + numberOfListings;

    const thisListingItem = document.getElementsByClassName(entryName)[0]; // eslint-disable-line prefer-destructuring
    const thisBounding = thisListingItem.getBoundingClientRect();

    const boundingLandSearch = document
      .getElementsByClassName('land-search')[0]
      .getBoundingClientRect();
    const boundingResultsInfo = document
      .getElementsByClassName('results-info')[0]
      .getBoundingClientRect();
    const boundingListingsPaging = document
      .getElementsByClassName('listings-paging')[0]
      .getBoundingClientRect();
    const boundingListingsResults = document
      .getElementsByClassName('listings-results')[0]
      .getBoundingClientRect();

    const scrollDistance =
      boundingLandSearch.bottom -
      boundingLandSearch.top +
      (boundingResultsInfo.bottom - boundingResultsInfo.top);
    const sizeOfNavigationContainer =
      boundingListingsPaging.top - boundingResultsInfo.bottom;
    const distanceToBottomOfContainer =
      boundingListingsResults.bottom - thisBounding.top;

    if (distanceToBottomOfContainer - sizeOfNavigationContainer >= 0) {
      //Normal scrolling.  Scroll this element into top position, if not already visible.
      if (
        thisBounding.top < scrollDistance ||
        thisBounding.bottom >
          (window.innerHeight || document.documentElement.clientHeight)
      ) {
        window.scrollTo({
          top: thisListingItem.offsetTop - scrollDistance,
          behavior: 'smooth',
        });
      }
    } else {
      //For entries near the bottom, scroll only to the last entry.  This prevents scrolling to the footer.
      const elementLastEntry = // eslint-disable-line prefer-destructuring
        document.getElementsByClassName(lastEntryName)[0];
      if (elementLastEntry) {
        const actualHeightOfLastEntry =
          elementLastEntry.getBoundingClientRect().top -
          elementLastEntry.getBoundingClientRect().bottom;
        const countOffset =
          boundingListingsPaging.top + actualHeightOfLastEntry;
        window.scrollTo({
          top: elementLastEntry.offsetTop - countOffset,
          behavior: 'smooth',
        });
      }
    }
  };

  const pageContext = useContext(PageContext);

  return (
    <LandSearchStyles>
      <Head htmlAttributes={{ lang: 'en' }}>
        <title>Find Land Near You | Clayton</title>
        <meta
          name="description"
          content="Search property listings in your area."
        />
        <meta
          property="og:image"
          content={getSiteUrl('/public-assets/images/california-hills.jpg')}
        />
        <meta
          name="twitter:image"
          content={getSiteUrl('/public-assets/images/california-hills.jpg')}
        />
        <meta
          property="og:description"
          content="Search property listings in your area."
        />
        <meta property="og:type" content="website" />
      </Head>
      <AreaHeader headerText="Find Land for Sale" />
      <div
        className={
          'land-search ' +
          ((showLocationModal || showPriceModal || showSortModal) && 'open')
        }
        ref={searchBarRef}
        onTouchStart={endSearchBarScroll}
        onTouchEnd={endSearchBarScroll}
        onContextMenu={(e) => {
          if (window.innerWidth < desktop_breakpoint) {
            e.preventDefault();
          }
        }}
        onScroll={checkSearchBarScrollState}
      >
        {!isSearchBarScrollMin && (
          <span
            onTouchStart={(e) => {
              startSearchBarScroll(-5);
              e.stopPropagation();
            }}
            onTouchEnd={(e) => {
              endSearchBarScroll();
              e.stopPropagation();
            }}
            onTouchCancel={(e) => {
              endSearchBarScroll();
              e.stopPropagation();
            }}
            className="left-scroll"
          >
            <ChevronLeft />
          </span>
        )}
        <div className="search-parameters">
          <div className="location-display">
            <div
              className={'search-pill ' + (showLocationModal && 'open')}
              tabIndex="0"
              onClick={() => {
                if (!touchTimeout) {
                  pageContext.state.scrollDirection = 'down'; //hide mobileNavBar
                  setShowLocationModal(!showLocationModal);
                  setZipErrors({});
                  setEnteredZip(zip);
                  setEnteredRadius(radius);
                  setShowPriceModal(false);
                  setShowSortModal(false);
                }
              }}
            >
              <div>
                <LocationPin />
              </div>
              <span className="standard">
                {radius} miles of {zip}
              </span>
              <div>
                <ChevronDown />
              </div>
            </div>
          </div>
        </div>
        <div className="search-parameters">
          <div className="price-display">
            <span
              key="search-pill"
              className={
                'standard search-pill ' +
                (showPriceModal && 'open') +
                (minPrice !== 0 || maxPrice != 40000 ? ' changed' : '')
              }
              tabIndex="0"
              onClick={() => {
                if (!touchTimeout) {
                  setShowPriceModal(!showPriceModal);
                  setShowLocationModal(false);
                  setShowSortModal(false);
                  setEnteredMaxPrice(maxPrice);
                  setEnteredMinPrice(minPrice);
                }
              }}
            >
              {`$${formatNumberWithCommas(minPrice) || 0}`} -{' '}
              {parseInt(maxPrice)
                ? `$${formatNumberWithCommas(maxPrice)}`
                : 'Any'}
            </span>
          </div>
        </div>
        {!isSearchBarScrollMax && (
          <span
            onTouchStart={(e) => {
              startSearchBarScroll(5);
              e.stopPropagation();
            }}
            onTouchEnd={(e) => {
              endSearchBarScroll();
              e.stopPropagation();
            }}
            onTouchCancel={(e) => {
              endSearchBarScroll();
              e.stopPropagation();
            }}
            className="right-scroll"
          >
            <ChevronRight />
          </span>
        )}
      </div>
      <div className="land-modals">
        {showLocationModal && (
          <div className="location-modal">
            <AnchoredModal
              autoPosition={true}
              showClearButton={false}
              className="location-modal"
              onClearButtonClick={() => {
                setShowLocationModal(false);
                setRadius(5);
              }}
              onApplyButtonClick={() => {
                if (validateZip()) {
                  if (enteredZip != zip || enteredRadius != radius) {
                    setZip(enteredZip);
                    setRadius(enteredRadius);
                    setPageIndex(0);
                  }

                  setShowLocationModal(false);
                }
              }}
              onCloseClick={(e) => {
                setShowLocationModal(false);
                e.preventDefault();
              }}
            >
              <Form
                onSubmit={() => {
                  if (validateZip()) {
                    if (enteredZip != zip || enteredRadius != enteredRadius) {
                      setZip(enteredZip);
                      setRadius(enteredRadius);
                      setPageIndex(0);
                    }

                    setShowLocationModal(false);
                  }
                }}
              >
                <div className="within-miles">
                  <span>Search within</span>
                  <DropDownList
                    dropDownStyle="compact"
                    className="radius-dropdown"
                    items={radii}
                    onChange={(item) => {
                      setEnteredRadius(item.value);
                    }}
                    selectedValue={enteredRadius}
                  />
                  <span>of</span>
                </div>
                <CityLocationSuggestion
                  dropDownStyle="compact"
                  id={zip}
                  errorMessage={zipErrors.zip}
                  onChange={(zip) => setEnteredZip(zip)}
                />
              </Form>
            </AnchoredModal>
          </div>
        )}
        {showPriceModal && (
          <div className="price-modal">
            <AnchoredModal
              autoPosition={true}
              onApplyButtonClick={() => {
                if (
                  enteredMinPrice != minPrice ||
                  enteredMaxPrice != maxPrice
                ) {
                  setMinPrice(enteredMinPrice);
                  setMaxPrice(enteredMaxPrice);
                  setPageIndex(0);
                }
                setShowPriceModal(false);
              }}
              onClearButtonClick={() => {
                setShowPriceModal(false);
                setMinPrice(0);
                setMaxPrice(40000);
              }}
              onCloseClick={(e) => {
                setShowPriceModal(false);
                e.preventDefault();
              }}
            >
              <div className="price-dropdowns">
                <DropDownList
                  dropDownStyle="compact"
                  items={minPrices}
                  onChange={(item) => {
                    if (item.value >= enteredMaxPrice) {
                      if (item.value + 10000 > 100000) {
                        setEnteredMaxPrice('any');
                      } else {
                        setEnteredMaxPrice(item.value + 10000);
                      }
                    }

                    setEnteredMinPrice(item.value);
                  }}
                  selectedValue={enteredMinPrice}
                />
                <span>-</span>
                <DropDownList
                  dropDownStyle="compact"
                  items={maxPrices}
                  onChange={(item) => {
                    if (item.value <= enteredMinPrice) {
                      setEnteredMinPrice(item.value - 10000);
                    }

                    setEnteredMaxPrice(item.value);
                  }}
                  selectedValue={enteredMaxPrice}
                />
              </div>
            </AnchoredModal>
          </div>
        )}
      </div>
      <div className="land-results">
        <div className="land-listings">
          <div className="results-info">
            <div className="results-number">
              <span className="label large">
                RESULTS: {landListings && landListings.count}{' '}
              </span>
            </div>
            <div className="sort-display">
              <span className="label large">SORT BY: </span>
              <span
                tabIndex="0"
                className="label large sort-selected"
                onClick={() => {
                  setShowSortModal(!showSortModal);
                  setShowPriceModal(false);
                  setShowLocationModal(false);
                  setEnteredSortBy(sortBy);
                }}
              >
                {sortBy == 0 ? 'Lowest Price' : 'Highest Price'}
              </span>
              <ChevronDown
                onClick={() => {
                  setShowSortModal(!showSortModal);
                  setShowPriceModal(false);
                  setShowLocationModal(false);
                  setEnteredSortBy(sortBy);
                }}
              />
            </div>
            {showSortModal && (
              <div className="sort-modal">
                <AnchoredModal
                  autoPosition={true}
                  onApplyButtonClick={() => {
                    if (enteredSortBy != sortBy) {
                      setSortBy(enteredSortBy);
                    }
                    setShowSortModal(false);
                  }}
                  onClearButtonClick={() => {
                    setSortBy(0);
                    setShowSortModal(false);
                  }}
                  onCloseClick={(e) => {
                    setShowSortModal(false);
                    e.preventDefault();
                  }}
                >
                  <DropDownList
                    className="sort-dropdown"
                    dropDownStyle="compact"
                    items={sortOptions}
                    onChange={(item) => {
                      setEnteredSortBy(item.value);
                    }}
                    selectedValue={enteredSortBy}
                  />
                </AnchoredModal>
              </div>
            )}
            {isSearching && (
              <div className="listings-searching">
                <LoadingIndicator width="50" height="50" />
              </div>
            )}
          </div>
          <div className={'listings-results ' + (isSearching && 'searching')}>
            {landListings &&
              landListings.listings?.map((listing, index) => (
                <LandListingItem
                  pinNumber={index + 1}
                  onClick={() => {
                    handleClickLeftNavigator(listing);
                  }}
                  searchPath={getSearchPath()}
                  key={`listing-${listing.listingKey}-${index + 1}`}
                  listing={listing}
                  selected={listing === selectedListing}
                />
              ))}
            {landListings && landListings.count === 0 && (
              <div className="empty-results constrained-width caption">
                No properties are currently available within your search
                parameters.
              </div>
            )}
          </div>
          <Link
            href="/find-land/terms-of-use"
            className="standard listhub-terms"
          >
            ListHub Data Terms of Use
          </Link>
          {landListings && landListings.pageCount > 0 && (
            <div className="listings-paging">
              <div className={'left ' + (pageIndex === 0 && 'disabled')}>
                <ChevronLeft
                  tabIndex="0"
                  viewBox="0 0 16 16"
                  onClick={() => {
                    if (pageIndex > 0) {
                      setPageIndex(pageIndex - 1);
                    }
                  }}
                />
              </div>
              <span className="label large">
                {landListings.pageIndex + 1} of {landListings.pageCount}
              </span>
              <div
                className={
                  'right ' +
                  (pageIndex == landListings.pageCount - 1 && 'disabled')
                }
              >
                <ChevronRight
                  tabIndex="0"
                  viewBox="0 0 16 16"
                  onClick={() => {
                    if (pageIndex < landListings.pageCount - 1) {
                      setPageIndex(pageIndex + 1);
                    }
                  }}
                />
              </div>
            </div>
          )}
        </div>
        <div className="land-map">
          {showMap && (
            <MapComponent mapOptions={mapOptions}>
              {landListings?.listings.map((listing, index) => {
                return (
                  <LocationMarker
                    selected={listing === selectedListing}
                    key={`land-marker-${index}`}
                    location={{
                      latitude: listing.geocodeLocation.latitude,
                      longitude: listing.geocodeLocation.longitude,
                    }}
                    number={String(index + 1)}
                    onClick={() => {
                      handleClickMapNavigator(
                        listing,
                        index,
                        landListings.listings.length
                      );
                    }}
                  />
                );
              })}
            </MapComponent>
          )}
        </div>
      </div>
    </LandSearchStyles>
  );
};

LandSearch.propTypes = {
  ssrLandListings: PropTypes.object,
  ssrSearchPath: PropTypes.string,
};

export async function getServerSideProps({ req, query }) {
  let landListings = null;
  const searchParams = getInitialSearchParams(
    typeof req !== 'undefined' ? query : null
  );

  if (searchParams.zip) {
    await searchLand({
      zipCode: searchParams.zip,
      radius: searchParams.radius,
      minPrice: searchParams.minPrice,
      maxPrice: parseInt(searchParams.maxPrice) || null,
      resultsPerPage: 25,
      pageIndex: searchParams.page,
      sortBy: searchParams.sortBy,
    })
      .then((result) => {
        landListings = result;
      })
      .catch((error) => {
        console.error(`Error retrieving listings: ${error}`);
      });
  } else {
    return {
      redirect: {
        destination: '/find-land',
        permanent: false,
      },
    };
  }

  return {
    props: {
      ssrLandListings: landListings,
      ssrSearchPath: req && req.url,
    },
  };
}

export default LandSearch;
